import {
  FC,
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import {
  applyNodeChanges,
  type EdgeTypes,
  NodeChange,
  type NodeTypes,
  type OnInit,
  type ReactFlowInstance,
} from '@xyflow/react';
import { getMapBy } from '../../../../common/utils';
import { AlgorithmTargetRuleType } from '../../../types';
import { AlgorithmGraph } from '../../AlgorithmGraph';
import { AlgorithmGraphCta } from '../../AlgorithmGraphCta';
import type { AlgorithmMappingQueryDtoType } from '../../mappings/schema/types';
import { AlgorithmType } from '../../schema/zAlgorithmType';
import { AlgorithmSourceFormDataType } from '../../sources/schema/types';
import { AlgorithmTargetRuleValidFormDataType } from '../schema/types';
import { AlgorithmTargetMappingGraphStore } from './AlgorithmTargetMappingGraphStore';
import { AlgorithmTargetMappingsGraphEdge } from './AlgorithmTargetMappingsGraphEdge';
import { AlgorithmTargetMappingsGraphSourceNode } from './AlgorithmTargetMappingsGraphSourceNode';
import { AlgorithmTargetMappingsGraphTargetNode } from './AlgorithmTargetMappingsGraphTargetNode';
import {
  AlgorithmTargetMappingEdge,
  AlgorithmTargetMappingNode,
  AlgorithmTargetMappingNodeData,
} from './types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmTargetMappingsGraph.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-target-mappings-graph',
);
addTranslations(translations);

const nodeTypes: NodeTypes = {
  source: AlgorithmTargetMappingsGraphSourceNode,
  target: AlgorithmTargetMappingsGraphTargetNode,
};
const edgeTypes: EdgeTypes = {
  default: AlgorithmTargetMappingsGraphEdge,
};

export interface AlgorithmTargetMappingsGraphRef {
  add: () => void;
}

export interface AlgorithmTargetMappingsGraphBaseProps
  extends PropsWithChildren {
  algorithmTypeCode: AlgorithmType;
  sources?: AlgorithmSourceFormDataType[];
  mappings?: AlgorithmMappingQueryDtoType[];
  rules: AlgorithmTargetRuleValidFormDataType[];
  isLoading?: boolean;
  ref?: RefObject<AlgorithmTargetMappingsGraphRef>;
}

export type AlgorithmTargetMappingsGraphProps =
  AlgorithmTargetMappingsGraphBaseProps &
    (
      | {
          onAdd: () => void;
          onRemove: (index: number) => void;
          readOnly?: false;
        }
      | {
          onAdd?: never;
          onRemove?: never;
          readOnly: true;
        }
    );

export const AlgorithmTargetMappingsGraph: FC<
  AlgorithmTargetMappingsGraphProps
> = (props) => {
  const {
    algorithmTypeCode,
    sources = [],
    mappings = [],
    readOnly = false,
    isLoading,
  } = props;

  return (
    <AlgorithmTargetMappingGraphStore.Provider
      initialData={{
        algorithmTypeCode,
        sources,
        mappings,
        handleRemove: undefined!,
        readOnly,
        isLoading,
      }}
    >
      <AlgorithmTargetMappingsGraphInner {...props} />
    </AlgorithmTargetMappingGraphStore.Provider>
  );
};

type AlgorithmTargetMappingsGraphInnerProps = {
  rules: AlgorithmTargetRuleValidFormDataType[];
} & (
  | {
      onAdd: () => void;
      onRemove: (index: number) => void;
      readOnly?: false;
    }
  | {
      onAdd?: never;
      onRemove?: never;
      readOnly: true;
    }
);

const AlgorithmTargetMappingsGraphInner: FC<
  AlgorithmTargetMappingsGraphInnerProps
> = (props) => {
  const { rules, readOnly = false, onAdd, onRemove } = props;
  const { t } = useTranslation();

  const reactFlowInstance = useRef<ReactFlowInstance<
    AlgorithmTargetMappingNode,
    AlgorithmTargetMappingEdge
  > | null>(null);

  const handleInit: OnInit<
    AlgorithmTargetMappingNode,
    AlgorithmTargetMappingEdge
  > = useCallback((instance) => {
    reactFlowInstance.current = instance;
    void instance.fitView();
  }, []);

  const handleAdd = useCallback(() => {
    if (!readOnly) {
      onAdd?.();
    }
  }, [readOnly, onAdd]);

  const [, setHandleRemove] =
    AlgorithmTargetMappingGraphStore.useValue('handleRemove');

  useEffect(() => {
    if (!readOnly) {
      setHandleRemove(onRemove);
    }
  }, [readOnly, setHandleRemove, onRemove]);

  const [nodeChanges, setNodeChanges] = useState<
    NodeChange<AlgorithmTargetMappingNode>[]
  >([]);

  const [nodes, edges] = useMemo(() => {
    const existingNodes = reactFlowInstance.current?.getNodes();
    return rulesToNodesAndEdges(
      rules,
      existingNodes &&
        (nodeChanges.length
          ? applyNodeChanges(nodeChanges, existingNodes)
          : existingNodes),
    );
  }, [rules, nodeChanges]);

  useEffect(() => {
    const timer = setTimeout(() => {
      reactFlowInstance.current?.fitView();
    });
    return () => {
      clearTimeout(timer);
    };
  }, [nodes, edges]);

  return (
    <Container>
      <AlgorithmGraph
        nodes={nodes}
        edges={edges}
        onNodesChange={setNodeChanges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onInit={handleInit}
      >
        {nodes.length === 0 && (
          <AlgorithmGraphCta
            heading={t('cta')}
            button={
              <Button
                id="algorithm-graph-cta"
                type="primary"
                onClick={handleAdd}
              >
                {t('button.create-first-algorithm-target-mapping')}
              </Button>
            }
          >
            {readOnly ? <h5>{t('no-mappings')}</h5> : null}
          </AlgorithmGraphCta>
        )}
      </AlgorithmGraph>
    </Container>
  );
};

const Container = styled.div`
  height: calc(100vh - 600px);
  min-height: 300px;
`;

const rulesToNodesAndEdges = (
  rules: AlgorithmTargetRuleValidFormDataType[],
  existingNodes?: AlgorithmTargetMappingNode[],
) => {
  const existingNodesById =
    existingNodes && getMapBy(existingNodes, (n) => n.id);

  const nodes: AlgorithmTargetMappingNode[] = [];
  const edges: AlgorithmTargetMappingEdge[] = [];
  let lastY = 0;

  for (const [index, rule] of rules.entries()) {
    if (rule.ruleType === AlgorithmTargetRuleType.MAP) {
      const data: AlgorithmTargetMappingNodeData = {
        index,
        ruleCode: rule.ruleCode,
        sourceValue: rule.sourceItem,
        targetValue: rule.dimensionIndicators,
      };
      const sourceId = `algorithmTargetMapping-${rule.ruleCode}-source`;
      const targetId = `algorithmTargetMapping-${rule.ruleCode}-target`;

      const existingSourceNode = existingNodesById?.get(sourceId);
      const existingTargetNode = existingNodesById?.get(targetId);

      const newNodes = [
        {
          ...existingSourceNode,
          id: sourceId,
          type: 'source',
          data,
          position: {
            x: 40,
            y: lastY + 140,
            ...existingSourceNode?.position,
          },
        },
        {
          ...existingTargetNode,
          id: targetId,
          type: 'target',
          data,
          position: {
            x: 500,
            y: lastY + 140,
            ...existingTargetNode?.position,
          },
        },
      ];

      nodes.push(...newNodes);
      edges.push({
        id: `edge-${sourceId}-${targetId}`,
        source: sourceId,
        target: targetId,
        data,
      });

      lastY = Math.max(...newNodes.map((n) => n.position.y));
    }
  }

  return [nodes, edges] as const;
};
